import { Alert, Box, Chip, Typography } from "@mui/material";
import { MaterialReactTable, MRT_ColumnDef } from "material-react-table";
import { useMemo } from "react";
import { FormattedMessage as T, useIntl } from "react-intl";
import { ImportEntityNode, ImportJobGraph } from "services/KnowfullyAdminClient";
import GeneratedIdCell from "./GeneratedIdCell";
import ImportStatusChip from "./ImportStatusChip";
import {
  entityTreeTableProps,
  ImportEntityNodeModel,
  mapToEntityTree,
  sharedEntities,
} from "./importEntityTreeHelpers";

const styles = {
  entity: {
    display: "flex",
    alignItems: "center",
    gap: 1,
  },
  type: {
    color: "text.secondary",
    fontSize: "0.78rem",
  },
  name: {
    fontFamily: "monospace",
    fontSize: "0.82rem",
  },
  generatedId: {
    fontFamily: "monospace",
    fontSize: "0.78rem",
  },
  message: {
    color: "text.secondary",
    fontSize: "0.82rem",
  },
  band: {
    mt: 4,
    mb: 1,
  },
};

type ImportsEntityTreeTableProps = {
  graph?: ImportJobGraph;
  isLoading?: boolean;
};

export default function ImportsEntityTreeTable({
  graph,
  isLoading,
}: Readonly<ImportsEntityTreeTableProps>) {
  const { formatMessage: t } = useIntl();

  const tree = useMemo(() => mapToEntityTree(graph?.nodes), [graph?.nodes]);
  const shared = useMemo(() => sharedEntities(graph?.nodes), [graph?.nodes]);

  const columns = useMemo<MRT_ColumnDef<ImportEntityNodeModel>[]>(
    () => [
      {
        accessorKey: "displayName",
        header: t({ id: "entityLabel" }),
        Cell: ({ row }) => <EntityCell node={row.original} />,
      },
      {
        accessorKey: "jobDataStatus",
        header: t({ id: "statusLabel" }),
        size: 130,
        Cell: ({ row }) => (
          <ImportStatusChip status={row.original.jobDataStatus} />
        ),
      },
      {
        accessorKey: "generatedId",
        header: t({ id: "generatedIdLabel" }),
        size: 150,
        Cell: ({ row }) => (
          <GeneratedIdCell
            jobDataType={row.original.jobDataType}
            generatedId={row.original.generatedId}
          />
        ),
      },
      {
        accessorKey: "message",
        header: t({ id: "messageLabel" }),
        Cell: ({ row }) => (
          <Box sx={styles.message}>{row.original.message}</Box>
        ),
      },
    ],
    [t]
  );

  if (!isLoading && tree.length === 0 && shared.length === 0) {
    return (
      <Alert severity="info">
        <T id="noEntitiesMessage" />
      </Alert>
    );
  }

  return (
    <>
      <MaterialReactTable
        {...entityTreeTableProps}
        columns={columns}
        data={tree}
        state={{ isLoading, density: "compact" }}
        renderTopToolbarCustomActions={() => (
          <Typography variant="h6" component="div">
            <T id="importedEntitiesLabel" />
          </Typography>
        )}
      />

      {shared.length > 0 && (
        <>
          <Box sx={styles.band}>
            <Typography variant="h6" component="div">
              <T id="sharedAcrossImportLabel" />
            </Typography>
          </Box>
          <MaterialReactTable
            {...entityTreeTableProps}
            enableExpanding={false}
            enableExpandAll={false}
            columns={columns}
            data={shared}
            state={{ density: "compact" }}
          />
        </>
      )}
    </>
  );
}

const EntityCell = ({ node }: { node: ImportEntityNode }) => {
  const { formatMessage: t } = useIntl();

  return (
    <Box sx={styles.entity}>
      <Box component="span" sx={styles.type}>
        {node.jobDataType}
      </Box>
      <Box component="span" sx={styles.name}>
        {node.displayName}
      </Box>
      {node.isShared && (node.referencedBySkuDetailIds?.length ?? 0) > 1 && (
        <Chip
          size="small"
          variant="outlined"
          label={t(
            { id: "sharedBySkusLabel" },
            { count: node.referencedBySkuDetailIds!.length }
          )}
        />
      )}
    </Box>
  );
};

